// Real-pixel-art sprite composer. Builds layered 64x64 ball sprites with
// smooth gradient bodies and pixel-art accessories tied to emergent traits.
//
// Layer stack (back-to-front):
//   1. body silhouette  — smooth arc + radial gradient, one of 4 shapes
//   2. eyes             — pixel-art (2x scale), one of 5 expressions
//   3. accessory        — pixel-art (2x scale), drawn from strongest emergent
//
// The canvas is 64x64 (2x the logical 32-unit drawing space). Bodies use the
// canvas arc/gradient API so they're smooth and vibrant at display size.
// Accessories + eyes use fillRect at integer logical coords and appear as
// chunky pixel-art details on top of the smooth body.
// Textures are added with LINEAR filtering so upscaled display stays crisp.

import Phaser from 'phaser';
import type { Genome } from '../sim/genome';
import { genomeKey } from '../sim/genome';
import { traitIndex } from '../sim/traits/base';

const SPRITE_SIZE = 64;
const DRAW_SCALE = 2; // logical drawing space is 32x32
const MAX_CACHE = 256;

// --- Palette catalog -------------------------------------------------------

interface Palette {
  outline: string;
  dark: string;
  mid: string;
  light: string;
  accent: string;
  eyeWhite: string;
  eyeDark: string;
}

const PALETTES: readonly Palette[] = [
  {
    // Magma
    outline: '#2b0d10',
    dark: '#8a1f1f',
    mid: '#d44a2e',
    light: '#f7c66b',
    accent: '#ffe8a8',
    eyeWhite: '#fff3d6',
    eyeDark: '#1b0508',
  },
  {
    // Frost
    outline: '#0d1f2e',
    dark: '#2c5a87',
    mid: '#5fa8d3',
    light: '#a8dfff',
    accent: '#ffffff',
    eyeWhite: '#e8f6ff',
    eyeDark: '#08182c',
  },
  {
    // Forest
    outline: '#1a2e10',
    dark: '#356b21',
    mid: '#6ab33d',
    light: '#c5e87a',
    accent: '#f7df5e',
    eyeWhite: '#f3f9d8',
    eyeDark: '#0a1607',
  },
  {
    // Royal
    outline: '#26102c',
    dark: '#6c2a8c',
    mid: '#b057d9',
    light: '#e9b6ff',
    accent: '#ffe0ff',
    eyeWhite: '#fdebff',
    eyeDark: '#15071c',
  },
  {
    // Earth
    outline: '#2e1e10',
    dark: '#7a4a1f',
    mid: '#c8884b',
    light: '#eecfa0',
    accent: '#f3e7b8',
    eyeWhite: '#fff3d6',
    eyeDark: '#1c0e05',
  },
  {
    // Steel
    outline: '#0c1014',
    dark: '#3a4452',
    mid: '#7a8a99',
    light: '#c1d0db',
    accent: '#ffe66b',
    eyeWhite: '#eef3f7',
    eyeDark: '#070b10',
  },
];

// --- Accessory catalog -----------------------------------------------------

type AccessoryDrawer = (ctx: CanvasRenderingContext2D, p: Palette) => void;

// Coordinates are in logical 32-unit space; ctx.scale(2,2) scales them up.
const ACCESSORIES: Record<string, AccessoryDrawer> = {
  berserker: (ctx) => {
    ctx.fillStyle = '#ff3030';
    ctx.fillRect(10, 13, 1, 1);
    ctx.fillRect(11, 14, 1, 1);
    ctx.fillRect(12, 15, 1, 1);
    ctx.fillRect(13, 16, 1, 1);
    ctx.fillStyle = '#8a0000';
    ctx.fillRect(11, 15, 1, 1);
    ctx.fillRect(12, 16, 1, 1);
  },
  'glass-cannon': (ctx, p) => {
    ctx.fillStyle = '#a8e8ff';
    ctx.fillRect(15, 5, 2, 1);
    ctx.fillRect(14, 6, 4, 1);
    ctx.fillStyle = '#5fc4ee';
    ctx.fillRect(15, 7, 2, 1);
    ctx.fillStyle = p.outline;
    ctx.fillRect(14, 5, 1, 1);
    ctx.fillRect(17, 5, 1, 1);
  },
  elder: (ctx) => {
    ctx.fillStyle = '#f5f0e0';
    ctx.fillRect(11, 6, 1, 1);
    ctx.fillRect(12, 5, 1, 1);
    ctx.fillRect(13, 6, 1, 1);
    ctx.fillRect(18, 6, 1, 1);
    ctx.fillRect(19, 5, 1, 1);
    ctx.fillRect(20, 6, 1, 1);
    ctx.fillStyle = '#cccccc';
    ctx.fillRect(12, 6, 1, 1);
    ctx.fillRect(19, 6, 1, 1);
  },
  shepherd: (ctx) => {
    ctx.fillStyle = '#ffe066';
    ctx.fillRect(13, 4, 6, 1);
    ctx.fillRect(13, 5, 1, 1);
    ctx.fillRect(18, 5, 1, 1);
    ctx.fillRect(13, 6, 6, 1);
    ctx.fillStyle = '#fff6b0';
    ctx.fillRect(14, 4, 4, 1);
  },
  'iron-hide': (ctx) => {
    ctx.fillStyle = '#7d8a99';
    ctx.fillRect(8, 17, 16, 1);
    ctx.fillRect(8, 18, 16, 1);
    ctx.fillStyle = '#aab7c6';
    ctx.fillRect(9, 17, 14, 1);
    ctx.fillStyle = '#3e4a5a';
    ctx.fillRect(8, 19, 16, 1);
    ctx.fillStyle = '#ffd24a';
    ctx.fillRect(11, 18, 1, 1);
    ctx.fillRect(15, 18, 1, 1);
    ctx.fillRect(19, 18, 1, 1);
  },
  pulsar: (ctx) => {
    ctx.fillStyle = '#ffec5c';
    ctx.fillRect(15, 15, 2, 2);
    ctx.fillStyle = '#fff7c2';
    ctx.fillRect(15, 15, 1, 1);
    ctx.fillStyle = '#ffaa1a';
    ctx.fillRect(13, 15, 1, 1);
    ctx.fillRect(18, 15, 1, 1);
    ctx.fillRect(15, 13, 1, 1);
    ctx.fillRect(15, 18, 1, 1);
  },
  'pack-leader': (ctx) => {
    ctx.fillStyle = '#ffd14a';
    ctx.fillRect(11, 6, 1, 2);
    ctx.fillRect(13, 6, 1, 2);
    ctx.fillRect(15, 5, 2, 3);
    ctx.fillRect(18, 6, 1, 2);
    ctx.fillRect(20, 6, 1, 2);
    ctx.fillRect(11, 8, 10, 1);
    ctx.fillStyle = '#a37510';
    ctx.fillRect(11, 9, 10, 1);
    ctx.fillStyle = '#e64ad9';
    ctx.fillRect(15, 6, 1, 1);
    ctx.fillStyle = '#4ae6c2';
    ctx.fillRect(16, 6, 1, 1);
  },
  phoenix: (ctx) => {
    ctx.fillStyle = '#ff8c20';
    ctx.fillRect(5, 14, 1, 4);
    ctx.fillRect(6, 13, 1, 6);
    ctx.fillRect(7, 12, 1, 8);
    ctx.fillRect(24, 12, 1, 8);
    ctx.fillRect(25, 13, 1, 6);
    ctx.fillRect(26, 14, 1, 4);
    ctx.fillStyle = '#ffd266';
    ctx.fillRect(6, 14, 1, 4);
    ctx.fillRect(25, 14, 1, 4);
  },
  frostbitten: (ctx) => {
    ctx.fillStyle = '#7ec8ff';
    ctx.fillRect(11, 18, 1, 1);
    ctx.fillRect(11, 19, 1, 1);
    ctx.fillRect(11, 20, 1, 2);
    ctx.fillStyle = '#bce3ff';
    ctx.fillRect(11, 18, 1, 1);
  },
  nursemaid: (ctx) => {
    ctx.fillStyle = '#ffa6c8';
    ctx.fillRect(9, 18, 2, 1);
    ctx.fillRect(21, 18, 2, 1);
    ctx.fillStyle = '#ff85b0';
    ctx.fillRect(9, 19, 2, 1);
    ctx.fillRect(21, 19, 2, 1);
  },
};

const ACCESSORY_PRIORITY = [
  'glass-cannon',
  'phoenix',
  'berserker',
  'pulsar',
  'pack-leader',
  'shepherd',
  'iron-hide',
  'elder',
  'frostbitten',
  'nursemaid',
] as const;

// --- Layer selection -------------------------------------------------------

export interface LayerSelection {
  paletteIndex: number;
  bodyShape: number;
  eyeStyle: number;
  accessoryId: string | null;
}

export function pickLayers(genome: Genome, emergentIds: readonly string[]): LayerSelection {
  const paletteAllele = genome[0] ?? 8;
  const sizeAllele = genome[traitIndex('size')] ?? 8;
  const xpAllele = genome[traitIndex('xpGain')] ?? 8;
  return {
    paletteIndex: paletteAllele % PALETTES.length,
    bodyShape: sizeAllele % 4,
    eyeStyle: xpAllele % 5,
    accessoryId: pickAccessory(emergentIds),
  };
}

function pickAccessory(ids: readonly string[]): string | null {
  if (ids.length === 0) return null;
  for (const candidate of ACCESSORY_PRIORITY) {
    if (ids.includes(candidate)) return candidate;
  }
  return null;
}

// --- Composer --------------------------------------------------------------

export class SpriteComposer {
  private readonly cache = new Map<string, string>();
  private readonly lru: string[] = [];
  private nextId = 0;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  constructor(private readonly scene: Phaser.Scene) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = SPRITE_SIZE;
    this.canvas.height = SPRITE_SIZE;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
  }

  textureFor(genome: Genome, emergentIds: readonly string[]): string {
    const k = genomeKey(genome) + '|' + (pickAccessory(emergentIds) ?? '');
    const hit = this.cache.get(k);
    if (hit !== undefined) {
      this.touch(k);
      return hit;
    }
    const layers = pickLayers(genome, emergentIds);
    const texKey = `ball:${this.nextId++}`;
    this.paint(layers);
    this.scene.textures.addCanvas(texKey, this.canvas);
    // LINEAR filter so upscaling looks smooth instead of nearest-neighbor blocky.
    this.scene.textures.get(texKey).setFilter(Phaser.Textures.FilterMode.LINEAR);
    this.cache.set(k, texKey);
    this.lru.push(k);
    if (this.cache.size > MAX_CACHE) this.evictOldest();
    return texKey;
  }

  destroy(): void {
    for (const tex of this.cache.values()) {
      if (this.scene.textures.exists(tex)) this.scene.textures.remove(tex);
    }
    this.cache.clear();
    this.lru.length = 0;
  }

  private touch(key: string): void {
    const i = this.lru.indexOf(key);
    if (i >= 0) this.lru.splice(i, 1);
    this.lru.push(key);
  }

  private evictOldest(): void {
    const oldest = this.lru.shift();
    if (!oldest) return;
    const tex = this.cache.get(oldest);
    if (tex && this.scene.textures.exists(tex)) this.scene.textures.remove(tex);
    this.cache.delete(oldest);
  }

  private paint(layers: LayerSelection): void {
    const { ctx } = this;
    ctx.clearRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);
    ctx.imageSmoothingEnabled = true;

    const palette = PALETTES[layers.paletteIndex]!;

    // Draw the smooth gradient body at native 64x64 coordinates.
    drawBody(ctx, palette, layers.bodyShape);

    // Pixel-art eyes and accessories are drawn in the 32-unit logical space;
    // scale(2,2) maps them onto the 64x64 canvas.
    ctx.save();
    ctx.scale(DRAW_SCALE, DRAW_SCALE);
    ctx.imageSmoothingEnabled = false;
    drawEyes(ctx, palette, layers.eyeStyle);
    if (layers.accessoryId) {
      const drawer = ACCESSORIES[layers.accessoryId];
      if (drawer) drawer(ctx, palette);
    }
    ctx.restore();
  }
}

// --- Body shapes (smooth gradient, drawn in 64x64 space) ------------------

function drawBody(ctx: CanvasRenderingContext2D, p: Palette, shape: number): void {
  // All coordinates in the 64x64 canvas space (logical x2).
  const cx = 32;
  const cy = 34;

  if (shape === 0) {
    // Round ball
    drawGradientCircle(ctx, cx, cy, 22, 20, p);
  } else if (shape === 1) {
    // Squashed oval
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(1, 0.72);
    drawGradientCircle(ctx, 0, 0, 22, 20, p);
    ctx.restore();
  } else if (shape === 2) {
    // Diamond
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(Math.PI / 4);
    drawGradientRect(ctx, 0, 0, 30, 30, 3, p);
    ctx.restore();
  } else {
    // Squircle (rounded square)
    drawGradientRect(ctx, cx, cy, 38, 38, 8, p);
  }
}

function drawGradientCircle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  p: Palette,
): void {
  // Outline ring
  ctx.beginPath();
  ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
  ctx.fillStyle = p.outline;
  ctx.fill();

  // Radial gradient body — highlight upper-right, shadow lower-left
  const hx = cx + outerR * 0.28;
  const hy = cy - outerR * 0.28;
  const grad = ctx.createRadialGradient(hx, hy, outerR * 0.05, cx, cy, innerR);
  grad.addColorStop(0, p.accent);
  grad.addColorStop(0.18, p.light);
  grad.addColorStop(0.55, p.mid);
  grad.addColorStop(0.88, p.dark);
  grad.addColorStop(1, p.outline);

  ctx.beginPath();
  ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
}

function drawGradientRect(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  w: number,
  h: number,
  corner: number,
  p: Palette,
): void {
  const x = cx - w / 2;
  const y = cy - h / 2;

  // Outline
  roundedRect(ctx, x - 1, y - 1, w + 2, h + 2, corner + 1);
  ctx.fillStyle = p.outline;
  ctx.fill();

  // Gradient fill
  const grad = ctx.createRadialGradient(
    cx + w * 0.15,
    cy - h * 0.15,
    h * 0.04,
    cx,
    cy,
    Math.max(w, h) * 0.62,
  );
  grad.addColorStop(0, p.accent);
  grad.addColorStop(0.2, p.light);
  grad.addColorStop(0.58, p.mid);
  grad.addColorStop(0.88, p.dark);
  grad.addColorStop(1, p.outline);

  roundedRect(ctx, x, y, w, h, corner);
  ctx.fillStyle = grad;
  ctx.fill();
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

// --- Eye expressions (drawn in 32-unit logical space, scaled x2) ----------

function drawEyes(ctx: CanvasRenderingContext2D, p: Palette, style: number): void {
  const yEye = 15;
  const xL = 12;
  const xR = 19;
  if (style === 0) {
    ctx.fillStyle = p.eyeDark;
    ctx.fillRect(xL, yEye, 2, 2);
    ctx.fillRect(xR, yEye, 2, 2);
    ctx.fillStyle = p.eyeWhite;
    ctx.fillRect(xL, yEye, 1, 1);
    ctx.fillRect(xR, yEye, 1, 1);
  } else if (style === 1) {
    ctx.fillStyle = p.eyeWhite;
    ctx.fillRect(xL - 1, yEye - 1, 4, 4);
    ctx.fillRect(xR - 1, yEye - 1, 4, 4);
    ctx.fillStyle = p.eyeDark;
    ctx.fillRect(xL, yEye, 2, 2);
    ctx.fillRect(xR, yEye, 2, 2);
    ctx.fillStyle = p.accent;
    ctx.fillRect(xL, yEye, 1, 1);
    ctx.fillRect(xR, yEye, 1, 1);
  } else if (style === 2) {
    ctx.fillStyle = p.eyeDark;
    ctx.fillRect(xL - 1, yEye + 1, 4, 1);
    ctx.fillRect(xR - 1, yEye + 1, 4, 1);
  } else if (style === 3) {
    ctx.fillStyle = p.eyeDark;
    ctx.fillRect(xL - 1, yEye, 4, 1);
    ctx.fillRect(xL, yEye + 1, 2, 1);
    ctx.fillRect(xR - 1, yEye, 4, 1);
    ctx.fillRect(xR, yEye + 1, 2, 1);
  } else {
    const ecx = 16;
    ctx.fillStyle = p.eyeWhite;
    ctx.fillRect(ecx - 2, yEye - 1, 5, 4);
    ctx.fillStyle = p.eyeDark;
    ctx.fillRect(ecx - 1, yEye, 3, 3);
    ctx.fillStyle = p.accent;
    ctx.fillRect(ecx, yEye, 1, 1);
  }
}
